import OrderNavigation from 'components/OrderNavigation/OrderNavigation';
import {
  AbsoluteWrapper,
  Container,
  ContentWrapper,
} from './SupplierNewOrderPage.styled';
import { tabs } from './constants';
import CompanyTab from '../NewOrdersFlowPages/CompanyTab/CompanyTab';
import { useNavigate, useLocation } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import ProductsTab from '../NewOrdersFlowPages/ProductsTab/ProductsTab';
import Loader from 'components/Loader/Loader';
import {
  OrderProductsSteps,
  OrderTabsEnum,
  ShippingType,
} from 'types/Order.types';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState, RootState } from 'store/store';
import {
  resetOrder,
  setActiveTab,
  setOrderProductsStep,
} from 'store/Order/actions/order';
import { useEffect, useRef, useState } from 'react';
import {
  IPostCartOrderDTO,
  useCheckIfCartExists,
  useCreateNewCart,
  usePostCartOrder,
} from './hooks';
import { ExistingCartModal } from 'components/Modals/ExistingCartModal/ExistingCartModal';
import { CTAModal } from 'components/Modals/CTAModal/CTAModal';
import { useTranslation } from 'react-i18next';
import OverviewTab from '../NewOrdersFlowPages/OverviewTab/OverviewTab';
import DetailsTab from '../NewOrdersFlowPages/DetailsTab/DetailsTab';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import {
  resetShoppingCart,
  setShoppingCartId,
} from 'store/ShoppingCart/actions/shoppingCart';
import { AppMode } from 'store/Common/reducers/common';

const SupplierNewOrderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isCTAModalOpen, setIsCTAModalOpen] = useState<boolean>(false);
  const [isLoadingCart, setIsLoadingCart] = useState<boolean>(false);
  const [isExistingCartModalOpen, setIsExistingCartModalOpen] =
    useState<boolean>(false);
  const { activeTab, companyId, orderProductsStep, detailsOrderData } =
    useSelector((state: RootState) => state.orderInfo);

  const { backFromBomPage } = location.state || {};

  const { shoppingCartId, cartLength } = useSelector(
    (state: RootState) => state.shoppingCartInfo
  );

  usePageTitle(t('New order'), true);

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const { mutate: createNewCart, isLoading: isLoadingCreatingNewCart } =
    useCreateNewCart(appMode);

  // Finish order got no answer that says what happened (a 504, a network
  // error): the order may have gone through (docs/BP-1219-ordering.md).
  const [isNoAnswerModalOpen, setIsNoAnswerModalOpen] =
    useState<boolean>(false);
  const { mutate: postCartOrder, isLoading: isLoadingPostingCartOrder } =
    usePostCartOrder(appMode, () => setIsNoAnswerModalOpen(true));
  // Set on the click itself, before react-query reports the request as loading:
  // a double click sends one order.
  const isPostingCartOrderRef = useRef<boolean>(false);

  const renderTabContent = () => {
    switch (activeTab) {
      case OrderTabsEnum.Company:
        return <CompanyTab />;
      case OrderTabsEnum.Products:
        return <ProductsTab />;
      case OrderTabsEnum.Overview:
        return <OverviewTab />;
      case OrderTabsEnum.Details:
        return <DetailsTab />;
      default:
        return null;
    }
  };

  const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const isLastTab = currentIndex === tabs.length - 1;
  const configFormIsShown =
    activeTab === OrderTabsEnum.Products &&
    orderProductsStep === OrderProductsSteps.Configuration;

  // Answered since the page opened or the buyer changed: never a check cached
  // by an earlier visit, whose cart may have been replaced (hooks.ts).
  const { data: cartExistsData, isFetchedAfterMount: isCartChecked } =
    useCheckIfCartExists(companyId, appMode);
  const triggeredCompanyIdRef = useRef<string>('');

  const handleCompanyTabNext = () => {
    const existingCartId = cartExistsData?.cart_id ?? null;
    const existingCartHasProducts =
      cartLength > 0 || (cartExistsData?.cart_length ?? 0) > 0;

    if (existingCartId && existingCartHasProducts) {
      // If cart exists and has products, store its id so "Continue with existing
      // cart" can load it, then let the user choose what to do.
      dispatch(setShoppingCartId(existingCartId));
      setIsLoadingCart(true);
      setIsExistingCartModalOpen(true);
    } else if (existingCartId) {
      // If cart exists but is empty, reuse it
      dispatch(setShoppingCartId(existingCartId));
      dispatch(setActiveTab(OrderTabsEnum.Products));
    } else {
      // If cart does not exist, create new cart
      createNewCart({ companyId: Number(companyId) });
    }
  };

  // Auto-trigger next action when company is selected on Company tab
  useEffect(() => {
    if (
      activeTab === OrderTabsEnum.Company &&
      companyId !== '' &&
      companyId !== triggeredCompanyIdRef.current &&
      isCartChecked
    ) {
      triggeredCompanyIdRef.current = companyId;
      handleCompanyTabNext();
    } else if (companyId === '') {
      triggeredCompanyIdRef.current = '';
    }
  }, [companyId, activeTab, isCartChecked, cartExistsData]);

  const handleNext = () => {
    if (activeTab === OrderTabsEnum.Company && companyId !== '') {
      handleCompanyTabNext();
    } else if (configFormIsShown) {
      setIsCTAModalOpen(true);
    } else {
      // For other tabs, just proceed to next
      if (currentIndex < tabs.length - 1) {
        dispatch(setActiveTab(tabs[currentIndex + 1].id));
      }
    }
  };

  const handleBack = () => {
    dispatch(setOrderProductsStep(OrderProductsSteps.Selection));
    if (currentIndex > 0) {
      if (
        activeTab === OrderTabsEnum.Products &&
        orderProductsStep === OrderProductsSteps.Configuration
      ) {
        dispatch(setActiveTab(OrderTabsEnum.Products));
      } else {
        dispatch(setActiveTab(tabs[currentIndex - 1].id));
      }
    }
  };

  useEffect(() => {
    if (!backFromBomPage) {
      dispatch(resetOrder());
      dispatch(resetShoppingCart());
      dispatch(setOrderProductsStep(OrderProductsSteps.Selection));
    }
  }, [backFromBomPage, dispatch]);

  const handleCreateOrder = () => {
    if (isPostingCartOrderRef.current) return;
    isPostingCartOrderRef.current = true;

    const payload: IPostCartOrderDTO = {
      cart_id: shoppingCartId,
      order_reference: detailsOrderData.order_reference,
      ...(detailsOrderData.requested_delivery_date && {
        requested_delivery_date: detailsOrderData.requested_delivery_date,
      }),
      ...(detailsOrderData.shipping_type === ShippingType.PREDEFINED && {
        shipping_address_code: detailsOrderData.shipping_address_code,
      }),
      ...(detailsOrderData.shipping_type === ShippingType.CUSTOM && {
        shipping_address_data: detailsOrderData.shipping_address_data,
      }),
      ...(detailsOrderData.shipping_type === ShippingType.PICKUP && {
        is_pickup: true,
      }),
    };

    postCartOrder(payload, {
      onSettled: () => {
        isPostingCartOrderRef.current = false;
      },
    });
  };

  return (
    <Container hasSubHeader={configFormIsShown}>
      <OrderNavigation
        tabs={tabs}
        activeTab={activeTab}
        finishedTabs={tabs
          .filter(
            (tab) =>
              tabs.findIndex((t) => t.id === tab.id) <
              tabs.findIndex((t) => t.id === activeTab)
          )
          .map((t) => t.id)}
        onNext={handleNext}
        onCreate={handleCreateOrder}
        disabledNext={
          !companyId ||
          (activeTab === OrderTabsEnum.Company && !isCartChecked) ||
          (isLastTab &&
            (!detailsOrderData.validDetails ||
              !detailsOrderData.requested_delivery_date)) ||
          (OrderTabsEnum.Overview === activeTab && !cartLength)
        }
        onBack={handleBack}
        backDestination={configFormIsShown ? 'Products' : undefined}
        loadingInProgress={
          isLoadingCart || isLoadingCreatingNewCart || isLoadingPostingCartOrder
        }
        disabledCancel={isLoadingPostingCartOrder}
        onClose={() => navigate(RoutesConfig.SupplierOrders.fullPath)}
      />

      {isLoadingCart ||
      isLoadingCreatingNewCart ||
      isLoadingPostingCartOrder ? (
        <AbsoluteWrapper>
          <Loader />
        </AbsoluteWrapper>
      ) : (
        <ContentWrapper hasSubHeader={configFormIsShown}>
          {renderTabContent()}
        </ContentWrapper>
      )}

      {isExistingCartModalOpen && (
        <ExistingCartModal
          isOpen={isExistingCartModalOpen}
          setIsOpen={setIsExistingCartModalOpen}
          onContinueWithExistingCart={() => {
            setIsExistingCartModalOpen(false);
            dispatch(setActiveTab(OrderTabsEnum.Overview));
            setIsLoadingCart(false);
          }}
          onCreateNewCart={() => {
            createNewCart({ companyId: Number(companyId) });
            setIsExistingCartModalOpen(false);
            setIsLoadingCart(false);
          }}
        />
      )}

      {isNoAnswerModalOpen && (
        <CTAModal
          isOpen={isNoAnswerModalOpen}
          setIsOpen={setIsNoAnswerModalOpen}
          firstButtonClick={() => setIsNoAnswerModalOpen(false)}
          firstButtonText={t('Close')}
          secondButtonClick={() => {
            setIsNoAnswerModalOpen(false);
            navigate(RoutesConfig.SupplierOrders.fullPath);
          }}
          secondButtonText={t('Go to orders')}
          title={t('No answer from the server')}
          description={t(
            'Your order may have gone through. Check your orders before you order these products again.'
          )}
        />
      )}

      {isCTAModalOpen && (
        <CTAModal
          isOpen={isCTAModalOpen}
          setIsOpen={setIsCTAModalOpen}
          firstButtonClick={() => {
            setIsCTAModalOpen(false);
          }}
          firstButtonText={t('Cancel')}
          secondButtonClick={() => {
            dispatch(setActiveTab(OrderTabsEnum.Overview));
            setIsCTAModalOpen(false);
          }}
          secondButtonText={t('Continue')}
          title={t('Continue to overview')}
          description={t(
            'Using this button will not save the current configuration. Are you sure you want to continue?'
          )}
        />
      )}
    </Container>
  );
};

export default SupplierNewOrderPage;
